import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import {
  SortHeader, Stat, frozen, freezePanes, timeZoneNote, TimeZoneNote,
  visibleSessions, summarizeSessions, TAX_ALL_SUBMODULES_VALUE,
} from './BPATracker';
// Moved to Configuration.js (2026-09-12) so the Project Registers grids share the same
// dirty-row/banding convention instead of keeping a second copy.
import { rowClassName } from './Configuration';

// Read-only customer view of Business Process Analysis (Jeremy, 2026-09-01; rebuilt 2026-09-02 to
// look like the internal grid rather than a card summary, per Jeremy's ask). Same `.sheet` table,
// frozen columns, sortable headers and time-zone note as BPATracker.js's Sessions/Detail grids --
// just no checkboxes, no inputs, no delete actions, and none of the internal-only columns
// (Config/Internal notes/Follow up/Owner).
//
// `GET /bpa-sessions`/`GET /bpa-details` already narrow to ADR 0017's field allow-list for an
// external caller (SessionsService/DetailsService), so this page renders whatever comes back
// without a second filtering pass of its own -- there is nothing internal-only left in the
// response by the time it gets here.

const SESSION_PANES = freezePanes([34, 48, 240]); // expand · # · Topic
const DETAIL_PANES = freezePanes([38, 200]); // # · Detail topic

// Mirrors BPATracker.js's STATUS_BADGE (not exported -- a two-way string map, not logic worth
// sharing) so a status reads the same badge color on both screens.
const SESSION_STATUS_BADGE = {
  'Not Scheduled': 'badge-gray',
  Scheduled: 'badge-blue',
  Completed: 'badge-green',
  'Follow-up Needed': 'badge-yellow',
  Canceled: 'badge-gray',
  'Not Applicable': 'badge-gray',
};

const DETAIL_STATUS_BADGE = {
  Open: 'badge-gray',
  Discussed: 'badge-blue',
  Decided: 'badge-green',
  Deferred: 'badge-yellow',
  'Not Applicable': 'badge-gray',
};

function fmtDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function DetailGrid({ details }) {
  if (!details.length) {
    return <div className="cell-read">Not discussed yet.</div>;
  }
  return (
    <div className="sheet-wrap">
      <table className="sheet">
        <thead>
          <tr>
            <th {...frozen(DETAIL_PANES, 0)}>#</th>
            <th {...frozen(DETAIL_PANES, 1)}>Detail topic</th>
            <th style={{ width: 320 }}>Discussion points</th>
            <th style={{ width: 320 }}>Customer questions</th>
            <th style={{ width: 340 }}>Customer response</th>
            <th style={{ width: 220 }}>Decision / outcome</th>
            <th style={{ width: 120 }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {details.map((d, index) => (
            <tr key={d.id} className={rowClassName(index, false)}>
              <td {...frozen(DETAIL_PANES, 0, 'cell-num')}>{d.detailNumber ?? '—'}</td>
              <td {...frozen(DETAIL_PANES, 1, 'cell-read')}>{d.detailTopic}</td>
              <td className="cell-read">{d.discussionPoints || '—'}</td>
              <td className="cell-read">{d.customerQuestions || '—'}</td>
              <td className="cell-read">{d.customerResponse || '—'}</td>
              <td className="cell-read">{d.decisionOutcome || '—'}</td>
              <td className="cell-read">
                <span className={`badge ${DETAIL_STATUS_BADGE[d.status] || 'badge-gray'}`}>{d.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CustomerBpa() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [vocabulary, setVocabulary] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [details, setDetails] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [sort, setSort] = useState({ key: 'sessionNumber', dir: 'asc' });
  const [filter, setFilter] = useState({ text: '', status: '', moduleId: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rows, vocab] = await Promise.all([
          apiFetch('/projects'),
          apiFetch('/bpa/vocabulary'),
        ]);
        if (cancelled) return;
        setProjects(rows);
        setVocabulary(vocab);
        if (rows.length === 1) setProjectId(rows[0].id);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!projectId) { setSessions(null); setDetails(null); return; }
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      apiFetch(`/bpa-sessions?projectId=${projectId}`),
      apiFetch(`/bpa-details?projectId=${projectId}`),
    ])
      .then(([sessionRows, detailRows]) => {
        if (cancelled) return;
        setSessions(sessionRows);
        setDetails(detailRows);
        setExpanded(new Set());
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  const detailsBySession = useMemo(() => {
    const map = new Map();
    (details || []).forEach((d) => {
      if (!map.has(d.sessionId)) map.set(d.sessionId, []);
      map.get(d.sessionId).push(d);
    });
    return map;
  }, [details]);

  const zoneNote = useMemo(() => {
    const account = projects.find((p) => p.id === projectId)?.account;
    const code = (account?.state ?? '').trim().toUpperCase();
    return timeZoneNote((vocabulary?.states ?? []).find((s) => s.code === code));
  }, [projects, projectId, vocabulary]);

  const toggleSort = (key) =>
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));

  // Sessions with their detail rows attached -- summarizeSessions (like the internal grid) reads
  // `s.details`, and this is also what the text filter's implicit search surface should match.
  const sessionsWithDetails = useMemo(
    () => (sessions ?? []).map((s) => ({ ...s, details: detailsBySession.get(s.id) || [] })),
    [sessions, detailsBySession],
  );

  // Distinct modules actually present on sessions, for the filter dropdown -- same reasoning as
  // the internal grid: filtering by a module with no sessions would just blank the grid with no
  // way to tell why.
  const sessionModules = useMemo(() => {
    const seen = new Map();
    for (const s of sessions ?? []) {
      if (s.module && !seen.has(s.module.id)) seen.set(s.module.id, s.module);
    }
    return [...seen.values()].sort((a, b) => a.code.localeCompare(b.code));
  }, [sessions]);

  const rows = useMemo(
    () => visibleSessions(sessionsWithDetails, { ...filter, sortKey: sort.key, sortDir: sort.dir }),
    [sessionsWithDetails, filter, sort],
  );
  const summary = useMemo(() => summarizeSessions(rows), [rows]);
  const filterActive = !!(filter.text.trim() || filter.status || filter.moduleId);

  const toggleExpanded = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services · Business Process Analysis</div>
        <h1 className="page-title">Business Process Analysis</h1>
        <p className="page-subtitle">Sessions, discussion topics and decisions for your project.</p>
      </div>

      {projects.length > 1 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="form-group" style={{ marginBottom: 0, maxWidth: 420 }}>
            <label className="form-label" htmlFor="customer-bpa-project">Project</label>
            <select
              id="customer-bpa-project"
              className="form-input"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">— Select a project —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {error && (
        <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid var(--red)' }} role="alert">
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            <strong style={{ color: 'var(--red)' }}>Couldn't load this page.</strong> {error}
          </div>
        </div>
      )}

      {loading && !sessions && (
        <div className="card" style={{ color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
      )}

      {!loading && !projects.length && !error && (
        <div className="placeholder-page">
          <div className="placeholder-icon">🗂️</div>
          <div className="placeholder-title">No projects available yet</div>
          <div className="placeholder-sub">Reach out to your Aumentum contact if this doesn't look right.</div>
        </div>
      )}

      {!loading && !error && projects.length > 1 && !projectId && (
        <div className="placeholder-page">
          <div className="placeholder-icon">📋</div>
          <div className="placeholder-title">Pick a project</div>
        </div>
      )}

      {sessions && !sessions.length && (
        <div className="placeholder-page">
          <div className="placeholder-icon">📋</div>
          <div className="placeholder-title">Nothing scheduled yet</div>
          <div className="placeholder-sub">Check back once business process analysis sessions are planned.</div>
        </div>
      )}

      {sessions && !!sessions.length && (
        <>
          <div className="stat-row">
            <Stat value={summary.sessionCount} label="Sessions" />
            <Stat value={summary.scheduledCount} label="Scheduled" />
            <Stat value={summary.completedCount} label="Completed" />
            <Stat value={`${summary.percentAnswered}%`} label="Rows answered" />
          </div>
          {filterActive && (
            <div style={{ fontSize: 12, color: 'var(--text-mid)', margin: '-8px 0 16px' }}>
              Filtered — these figures cover {summary.sessionCount} of {sessions.length} session(s).
            </div>
          )}

          <div className="card" style={{ marginBottom: 20 }}>
            <div className="sheet-toolbar" style={{ marginBottom: 0 }}>
              <input
                className="form-input"
                style={{ maxWidth: 240 }}
                placeholder="Filter topic, module or lead…"
                value={filter.text}
                onChange={(e) => setFilter((f) => ({ ...f, text: e.target.value }))}
              />
              <select
                className="form-select"
                style={{ maxWidth: 170 }}
                value={filter.status}
                onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                {(vocabulary?.sessionStatuses ?? []).map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
              <select
                className="form-select"
                style={{ maxWidth: 200 }}
                value={filter.moduleId}
                onChange={(e) => setFilter((f) => ({ ...f, moduleId: e.target.value }))}
                aria-label="Filter by module"
              >
                <option value="">All modules</option>
                <option value={TAX_ALL_SUBMODULES_VALUE}>Tax - All Submodules</option>
                {sessionModules.map((m) => (
                  <option key={m.id} value={m.id}>{m.code} — {m.name}</option>
                ))}
              </select>
              {(filter.text || filter.status || filter.moduleId) && (
                <button
                  className="btn btn-outline"
                  onClick={() => setFilter({ text: '', status: '', moduleId: '' })}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          <div className="card">
          <TimeZoneNote note={zoneNote} />

          <div className="sheet-wrap">
            <table className="sheet">
              <thead>
                <tr>
                  <th {...frozen(SESSION_PANES, 0)} />
                  <SortHeader label="#" k="sessionNumber" sort={sort} onSort={toggleSort} freeze={SESSION_PANES[1]} />
                  <SortHeader label="Topic" k="topic" sort={sort} onSort={toggleSort} freeze={SESSION_PANES[2]} />
                  <SortHeader label="Module" k="module" sort={sort} onSort={toggleSort} width={90} />
                  <SortHeader label="Status" k="status" sort={sort} onSort={toggleSort} width={150} />
                  <SortHeader label="Date" k="scheduledDate" sort={sort} onSort={toggleSort} width={130} />
                  <th style={{ width: 105 }}>Start</th>
                  <th style={{ width: 105 }}>End</th>
                  <th style={{ width: 70 }}>Onsite</th>
                  <SortHeader label="Led by" k="conductedBy" sort={sort} onSort={toggleSort} width={130} />
                  <th style={{ width: 130 }}>Meeting link</th>
                  <th style={{ width: 130 }}>Recording</th>
                  <th style={{ width: 60 }}>Rows</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s, index) => {
                  const sessionDetails = detailsBySession.get(s.id) || [];
                  return (
                    <React.Fragment key={s.id}>
                      <tr className={rowClassName(index, false)}>
                        <td {...frozen(SESSION_PANES, 0)}>
                          <button
                            className="expand-btn"
                            onClick={() => toggleExpanded(s.id)}
                            aria-label={expanded.has(s.id) ? 'Collapse' : 'Expand'}
                            style={{ padding: '7px 10px' }}
                          >
                            {expanded.has(s.id) ? '▾' : '▸'}
                          </button>
                        </td>
                        <td {...frozen(SESSION_PANES, 1, 'cell-num')}>{s.sessionNumber ?? '—'}</td>
                        <td {...frozen(SESSION_PANES, 2, 'cell-read')}>{s.topic}</td>
                        <td className="cell-read">{s.module?.code ?? '—'}</td>
                        <td className="cell-read">
                          <span className={`badge ${SESSION_STATUS_BADGE[s.status] || 'badge-gray'}`}>{s.status}</span>
                        </td>
                        <td className="cell-read">{fmtDate(s.scheduledDate)}</td>
                        <td className="cell-read">{s.startTime || '—'}</td>
                        <td className="cell-read">{s.endTime || '—'}</td>
                        <td className="cell-read">{s.deliveryMethod === 'Onsite' ? 'Onsite' : s.deliveryMethod === 'Remote' ? 'Remote' : '—'}</td>
                        <td className="cell-read">{s.conductedBy || '—'}</td>
                        <td className="cell-read">
                          {s.meetingLink ? <a href={s.meetingLink} target="_blank" rel="noreferrer">Join</a> : '—'}
                        </td>
                        <td className="cell-read">
                          {s.recordingLink ? <a href={s.recordingLink} target="_blank" rel="noreferrer">Watch</a> : '—'}
                        </td>
                        <td className="cell-num">{sessionDetails.length}</td>
                      </tr>
                      {expanded.has(s.id) && (
                        <tr>
                          <td colSpan={12} style={{ padding: '0 0 10px 34px', background: 'var(--light-bg)' }}>
                            <DetailGrid details={sessionDetails} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </div>
        </>
      )}
    </div>
  );
}
